import React from 'react';
import { Check, ExternalLink, Plus } from 'lucide-react';
import { SupplierView } from './discoveryModel';
import { listingKeyOf, productLabelOf } from './listingGroups';
import { websiteLabelOf } from './supplierWebsite';
import PartNumberChip from './PartNumberChip';
import { useReadOnly } from '../../contexts/WizardAccessContext';

/**
 * One supplier, several products matching the line (Element14 with two
 * modules): one compact row per product under the supplier's card — what it
 * is, the part-number check, its page, its price — each with the same
 * shortlist tick the cards use. Ticking one or both shortlists the supplier;
 * outreach asks about every ticked product in one email. The card's header
 * says how many ("2 products"); the rows add no box of their own.
 */
interface ListingRowsProps {
  listings: SupplierView[];
  tickedKeys: readonly string[];
  onToggle: (listingKey: string) => void;
  className?: string;
}

const priceText = (s: SupplierView): string =>
  s.quotedPrice != null ? `${s.currencySymbol}${s.quotedPrice.toLocaleString()}` : '—';

export const ShortlistTick: React.FC<{ ticked: boolean; onToggle: () => void; label?: string }> = ({ ticked, onToggle, label }) => {
  const readOnly = useReadOnly();
  const tone = ticked
    ? 'border-success-border bg-success-bg text-success-ink'
    : 'border-line-strong bg-surface text-ink-body hover:border-action hover:text-action';
  const classes = `flex shrink-0 items-center gap-[5px] whitespace-nowrap rounded-[7px] border px-s4 py-[5px] font-sans text-[11px] font-semibold ${tone}`;
  if (readOnly) {
    return ticked ? (
      <span className={classes}>
        <Check className="h-[11px] w-[11px]" /> Shortlisted
      </span>
    ) : null;
  }
  return (
    <button
      type="button"
      aria-pressed={ticked}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={classes}
    >
      {ticked ? <Check className="h-[11px] w-[11px]" /> : <Plus className="h-[11px] w-[11px]" />}
      {ticked ? 'Shortlisted' : label ?? 'Shortlist'}
    </button>
  );
};

export const ListingRows: React.FC<ListingRowsProps> = ({ listings, tickedKeys, onToggle, className = '' }) => (
  <ul className={`m-0 list-none border-t border-line-divider-soft p-0 ${className}`} aria-label={`${listings.length} products`}>
    {listings.map((s, i) => {
      const key = listingKeyOf(s);
      const label = productLabelOf(s, i);
      return (
        <li key={key} className="flex flex-wrap items-center gap-x-s3 gap-y-s1 border-b border-line-divider-soft py-[6px] last:border-b-0">
          <span className="min-w-0 flex-1 basis-[160px] truncate text-[12px] font-semibold text-ink max-md:whitespace-normal" title={label}>
            {label}
          </span>
          <PartNumberChip check={s.partNumberCheck} />
          {s.websiteUrl && (
            <a
              href={s.websiteUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-s1 text-meta text-ink-muted hover:text-ink-secondary"
            >
              <ExternalLink className="h-[11px] w-[11px]" /> {websiteLabelOf(s.websiteKind)}
            </a>
          )}
          <span className="font-mono text-[12px] font-semibold text-ink">{priceText(s)}</span>
          <ShortlistTick ticked={tickedKeys.includes(key)} onToggle={() => onToggle(key)} />
        </li>
      );
    })}
  </ul>
);

export default ListingRows;
